import React, {useContext, useEffect, useState} from 'react';
import {
  IonContent,
  IonPage,
  IonHeader, IonItem,
} from '@ionic/react';
import {
  getProperty,
  getBooking,
  getBookingRooms,
  getPropertyRooms,
  getBookingCount,
  setKey, getBookingNotes
} from '../utilities/Storage';
import moment from 'moment';
import {
  requestBooking,
  requestBookingNotes,
  requestBookingRooms,
  requestPropertyRooms,
  requestSamePropertyBookingsCount,
} from "../utilities/ApiTasks";
import { JobRow } from '../components/JobSelfEmployed/JobRow';
import { Extras } from '../components/JobSelfEmployed/Extras';
import { Rooms } from '../components/JobSelfEmployed/Rooms';
import {NoBookingDetails} from "../components/NoBookingDetails";
import {Toolbar} from "./Toolbar";
import {AuthContext} from "../contexts/AuthContext";
import {userIsCleaner} from "../dataStructures/user/predicates";

export default function Booking({history, match}) {
  const id = match.params.id;
  const [property, setProperty] = useState();
  const [booking, setBooking] = useState({});
  const [propertyRooms, setPropertyRooms] = useState({});
  const [bookingRooms, setBookingRooms] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [bookingName, setBookingName] = useState('');
  const [notes, setNotes] = useState([]);

  const { user } = useContext(AuthContext);
  const isCleaner = userIsCleaner(user);

  React.useEffect(() => {
    if (!isCleaner) {
      history.push('/jobs');
    }
  }, [history]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const getData = async () => {

      if (!booking || booking?.id !== id) {
        setBooking(null);
      }

      if (!isMounted) return;

      let bookingData = await getBooking(id);
      if(!bookingData.length) {
        bookingData = await requestBooking(id);
      }

      let bookingRooms = await getBookingRooms(id);
      if (!bookingRooms.length) {
        bookingRooms = await requestBookingRooms(id);
      }

      const propertyId = bookingData.property.id;
      const p = await getProperty(propertyId);
      setProperty(p);
      let propertyRooms = await getPropertyRooms(propertyId);
      if (!propertyRooms.length) {
        propertyRooms = await requestPropertyRooms(propertyId);
      }
      let jn = await getBookingNotes(id);
      if (!jn.length) {
        jn = await requestBookingNotes(id);
      }

      setBooking(bookingData);
      setBookingRooms(bookingRooms);
      setPropertyRooms(propertyRooms);


      let validNotes = [];
      if(jn) {
        for (const jobNote of jn) {
          if (jobNote.note.visibletocleaner || jobNote.note.visibletokeyholder  || jobNote.note.visibletoowner) {
            let texts = [];
            for (const text of jobNote.note.notetexts) {
              texts.push(text.notetext);
            }

            validNotes.push({
              subject: jobNote.note.subject,
              text: texts.join('; '),
              id: jobNote.id
            });
          }
        }
      }

      setNotes(validNotes);

      let otherBookingsAtTheSameProperty = 0;
      if (bookingData.guesttype === 'Customer'
          && bookingData.customers
          && bookingData.customers.length > 0
          && bookingData.property
          && bookingData.property.id
      ) {

        const hash = [bookingData.id, bookingData.customers[0].id, bookingData.property.id].join('|');
        otherBookingsAtTheSameProperty = await getBookingCount(hash);
        if (!isFinite(otherBookingsAtTheSameProperty)) {
          otherBookingsAtTheSameProperty = await requestSamePropertyBookingsCount(bookingData.id, bookingData.customers[0]?.id ?? null, bookingData.property.id);
        }
      }

      if (bookingData?.guesttype === "Owner") {
        setBookingName(`Owner Booking - ${bookingData.ownerbookingtype.description}`);
      } else if (bookingData?.guesttype === "Agency") {
        setBookingName(`Agency Booking - ${bookingData.ownerbookingtype.description}`);
      } else if (bookingData?.customers) {
        setBookingName(`${bookingData.customers[0].name ?? ''} - ${otherBookingsAtTheSameProperty} other bookings at the property`);
      } 

    }

    getData().then(() => {
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };

  }, [id]);

  if (!booking || !property) {
    return <NoBookingDetails isLoading={isLoading} />;
  }

  const pageTitle = `Booking ${id}`;

  return (
      <IonPage>
        <IonHeader>
          <Toolbar title={pageTitle} backButtonHref="/bookings" />
        </IonHeader>
        <IonContent>
          {booking?.id && (
              <>
                <JobRow label="Booking ID" value={booking.id} />
                <JobRow
                    label="Arrive"
                    value={moment(booking.fromdate, 'YYYY-MM-DD').format('dddd D MMMM YYYY')}
                />
                <JobRow
                    label="Depart"
                    value={moment(booking.todate, 'YYYY-MM-DD').format('dddd D MMMM YYYY')}
                />
                <JobRow
                    label="Duration"
                    value={`${moment(booking.todate, 'YYYY-MM-DD').diff(moment(booking.fromdate, 'YYYY-MM-DD'), 'days')} nights`}
                />
                <JobRow label="Customer" value={bookingName} />
                <JobRow
                    label="Ad/Ch/Inf/Pet"
                    value={`${booking.adults ?? 0}/${booking.children ?? 0}/${booking.infants ?? 0}/${booking.pets ?? 0}`}
                />
                {notes.length > 0 && (
                    <>
                      <IonItem color="lgrey" lines="none">
                        <h4 className="ion-no-margin">Rejected notes</h4>
                      </IonItem>

                      {notes.map((row) => (
                          <JobRow
                              key={row.id}
                              label={row.subject}
                              value={row.text}
                              color="pink"
                          />
                      ))}
                    </>
                )}
                <Extras bookingExtras={booking.extras ?? []} />
                {(bookingRooms.length > 0 || propertyRooms.length > 0) && (
                    <Rooms bookingRooms={bookingRooms} propertyRooms={propertyRooms} />
                )}
              </>
          )}

        </IonContent>
      </IonPage>
  );
}